import React, { createContext, forwardRef, useRef } from 'react';
import {
  TextField,
  TextFieldProps,
  Input as AriaInput,
  InputProps as AriaInputProps,
  ValidationResult,
} from 'react-aria-components';
import { FormValidationContext } from 'react-stately';
import styles from './Input.module.css';
import inputStyles from '../../../utils/forms/input/input.module.css';
import FieldHeader from '../../FieldHeader/FieldHeader';
import IconButton from '../../IconButton/IconButton';
import FieldError from '../../FieldError';
import { type InputFieldVariants } from '../../../utils/forms/input/input';
import { type WithResponsiveProps } from '../../../utils/breakpoint/responsiveSSR';
import { resolveValidationState } from '../../../utils/forms/resolveValidationState';
import {
  resolveSearchInputConfig,
  resolveInputFieldClasses,
  handleClearClick,
  assignInputRefs,
  resolveStartContent,
  resolveEndContent,
  shouldShowClearButton,
} from './__utils';
import {
  InputPrefix,
  InputSuffix,
  InputStartSlot,
  InputEndSlot,
  InputStartIcon,
  InputEndIcon,
  InputStartSelect,
  InputEndSelect,
} from './__subcomponents';
import {
  useInputSlotWidths,
  useInputChildren,
  useResponsiveInputField,
} from './__hooks';

// TODO: extract context
/**
 * Context provided to all Input subcomponents (Prefix, Suffix, StartSlot, etc.)
 * Allows subcomponents to adapt their rendering and styling based on the parent Input's state.
 * For example, icons inside a disabled input become grayed out automatically.
 */
export type InputContextType = {
  /**
   * Whether the input field is disabled (read-only, no interaction)
   * When true, subcomponents disable interactive elements like buttons
   */
  isDisabled?: boolean;

  /**
   * Whether the input field failed validation
   * Subcomponents may display visual feedback (e.g., red icons) when invalid
   */
  isInvalid?: boolean;

  /**
   * The HTML input type (e.g., 'text', 'email', 'password', 'search')
   * Used by subcomponents to conditionally render type-specific elements
   */
  type?: string;

  /**
   * Unique ID for the input element (generated via React.useId)
   * Used for accessibility: aria-labelledby, aria-describedby references
   */
  inputId?: string;
};

export const InputContext = createContext<InputContextType>({});

type ResponsivePropKeys = 'fieldColumns' | 'fieldMaxWidthByChars';
interface BaseInputProps extends Omit<TextFieldProps, 'children'> {
  /**
   * Custom field component to render (e.g., AriaNumberField)
   */
  as?: React.ComponentType<object>;
  /**
   * Label for the input field
   */
  label?: string;

  /**
   * Description text for the input
   */
  description?: string;

  /**
   * Placeholder text for the input
   */
  placeholder?: string;

  /**
   * Error message for the input
   */
  errorMessage?: string | ((validation: ValidationResult) => string);

  /**
   * Additional props to pass to the underlying input element
   */
  inputProps?: Omit<AriaInputProps, 'className'>;

  /**
   * Additional class name for the input wrapper
   */
  className?: string;

  /**
   * Children for Input subcomponents
   */
  children?: React.ReactNode;

  /**
   * Whether the input can be cleared
   * @default false
   */
  clearable?: boolean;

  /**
   * Tooltip text for the clear button
   * @default 'Clear input'
   */
  clearButtonTooltip?: string;

  /**
   * Accessible label for the clear button
   * @default 'Clear input'
   */
  clearButtonAriaLabel?: string;

  /**
   * The number of columns the Field should have
   */
  fieldColumns?: InputFieldVariants['fieldColumns'];
  /**
   * Field width based on a rough estimate of the number of characters
   */
  fieldMaxWidthByChars?: InputFieldVariants['fieldMaxWidthByChars'];
  /**
   * The placement of the error message
   * @default 'top'
   */
  errorPlacement?: 'top' | 'bottom' /**
   * The translation for the optional text, used for localization
   * @default 'Optional'
   */;
  optionalTranslation?: string;
  /**
   * The id of the error message element
   */
  errorId?: string;
  /**
   * Prefix text for screen readers only in error messages
   * @default 'Error:'
   */
  errorPrefix?: string;
  /**
   * Additional context for screen readers, rendered visually hidden next to the label.
   */
  visuallyHiddenLabel?: string;
}

export type InputProps = WithResponsiveProps<
  BaseInputProps,
  ResponsivePropKeys
>;

// Define the Input component type
type InputComponent = React.ForwardRefExoticComponent<
  InputProps & React.RefAttributes<HTMLInputElement>
> & {
  StartSlot: typeof InputStartSlot;
  EndSlot: typeof InputEndSlot;
  Prefix: typeof InputPrefix;
  Suffix: typeof InputSuffix;
  StartSelect: typeof InputStartSelect;
  EndSelect: typeof InputEndSelect;
  StartIcon: typeof InputStartIcon;
  EndIcon: typeof InputEndIcon;
};

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      description,
      placeholder,
      fieldColumns,
      fieldMaxWidthByChars,
      isInvalid,
      isDisabled = false,
      isRequired = false,
      inputProps,
      children,
      clearable = false,
      clearButtonAriaLabel = 'Clear input',
      clearButtonTooltip = clearButtonAriaLabel,
      optionalTranslation,
      errorMessage,
      errorId,
      errorPrefix,
      errorPlacement = 'top',
      as: FieldComponent = TextField,
      visuallyHiddenLabel,
      ...props
    },
    ref,
  ) => {
    // Get form-wide validation errors from React Stately context
    // This allows form submission handlers to propagate errors to individual fields
    const formValidationErrors = React.useContext(FormValidationContext);

    // Extract field name from props for form validation error lookups
    // Used to match this input field to errors in formValidationErrors object
    const fieldName = typeof props.name === 'string' ? props.name : undefined;

    // Resolve validation state priority: explicit props > form context > no error
    // If isInvalid prop is passed, use it. Otherwise check formValidationErrors.
    // This allows both local and form-level validation to control error display.
    const { effectiveErrorMessage, effectiveIsInvalid } =
      resolveValidationState({
        isInvalid,
        errorMessage,
        fieldName,
        validationErrors: formValidationErrors,
      });

    // Generate responsive CSS classes for different breakpoints
    // This hook consolidates all responsive field styling logic:
    // - baseColumns/baseMaxWidthByChars: SSR-safe defaults for initial render
    // - columnsResp/maxWidthByCharsResp: CSS class arrays for all breakpoints
    // Browser CSS media queries automatically switch between these classes.
    const {
      baseColumns,
      baseMaxWidthByChars,
      columnsResp,
      maxWidthByCharsResp,
    } = useResponsiveInputField(
      fieldColumns,
      fieldMaxWidthByChars,
      inputStyles,
    );

    // Resolve input type from either inputProps or general props (prefer inputProps)
    // Used to determine special handling for type="search" inputs
    const inputType = inputProps?.type || props.type;

    // Handle special behavior for type="search" inputs
    // Search inputs automatically get clearable=true and hide native clear button
    const { effectiveClearable, inputFieldType, inputFieldStyle } =
      resolveSearchInputConfig(clearable, inputType);

    // Generate unique ID for this input instance for a11y associations
    // Used by InputContext to connect icons, slots, and ARIA attributes
    const inputId = React.useId();

    // Refs to DOM elements for imperative interactions
    const inputElementRef = useRef<HTMLInputElement>(null); // The actual native input
    const clearButtonRef = useRef<HTMLDivElement>(null); // Clear button wrapper

    // Measure all slot elements and apply CSS custom properties for dynamic spacing
    // This hook runs in two phases:
    // 1. useLayoutEffect: Measures synchronously before paint for accurate initial layout
    // 2. useEffect + debounce: Re-measures after paint for responsive window resize events
    // The widths are injected as CSS vars (--input-start-slot-width, etc.) so the grid layout
    // can dynamically adjust padding based on actual rendered slot widths.
    const {
      wrapperRef,
      startSlotRef,
      endSlotRef,
      prefixRef,
      suffixRef,
      startSelectRef,
      endSelectRef,
      startIconRef,
      endIconRef,
    } = useInputSlotWidths(children);

    // Extract and type-check all Input subcomponent children
    // Searches through children to find specific InputPrefix, InputSuffix, InputStartSlot, etc.
    // Also detects if slots contain interactive buttons to trigger CSS padding adjustments
    // (buttons need extra space compared to text or icons)
    const {
      startSlotChildren,
      endSlotChildren,
      hasStartSlotButton,
      hasEndSlotButton,
      prefixChildren,
      suffixChildren,
      startSelectChildren,
      endSelectChildren,
      startIconChildren,
      endIconChildren,
    } = useInputChildren(children);

    // Determine if clear button should be shown by checking all required conditions:
    // 1. effectiveClearable: Feature enabled (user or type="search")
    // 2. inputProps?.value: Value exists (not null/undefined)
    // 3. Value is not empty: String length > 0
    // 4. !isDisabled: Input is enabled (disabled inputs can't be cleared)
    // This logic is encapsulated in a helper for testability and reusability.
    const shouldShowClearButtonFlag = shouldShowClearButton(
      effectiveClearable,
      inputProps?.value,
      isDisabled,
    );

    // Merge responsive column and width classes into final class array
    // - baseClass: Initial CVA class with base values (used for SSR)
    // - responsiveClasses: Array of responsive variant classes for all breakpoints
    // Example: ['input-field-variant--columns-twelve', 'input-field-variant--columns-six@md', ...]
    const { baseClass, responsiveClasses } = resolveInputFieldClasses(
      fieldColumns,
      fieldMaxWidthByChars,
      baseColumns,
      baseMaxWidthByChars,
      columnsResp,
      maxWidthByCharsResp,
    );

    const clearButtonElement = shouldShowClearButtonFlag ? (
      <div ref={clearButtonRef}>
        <IconButton
          iconProps={{
            iconName: 'circle-xmark',
            iconPrefix: 'fas',
          }}
          emphasis="quaternary"
          size="sm"
          styleVariant="neutral"
          onClick={(e) =>
            handleClearClick(
              e,
              inputElementRef as React.MutableRefObject<HTMLInputElement | null>,
              inputProps,
            )
          }
          aria-label={clearButtonAriaLabel}
          tooltipLabel={clearButtonTooltip}
        />
      </div>
    ) : null;

    return (
      <FieldComponent
        {...props}
        isDisabled={isDisabled}
        {...(effectiveErrorMessage || effectiveIsInvalid
          ? { isInvalid: effectiveIsInvalid }
          : {})}
        className={`
          ${styles['input-field']} 
        `}
      >
        {/* Render FieldHeader for label, description, and top-positioned errors */}
        {/* Show even if label is missing, as long as description exists or errors go at top */}
        {/* FieldHeader handles label, description, error display, and required/optional indicators */}
        {(label || description || errorPlacement === 'top') && (
          <FieldHeader
            label={label}
            labelId={inputProps?.['aria-labelledby']}
            labelFor={inputProps?.id}
            description={description}
            descriptionId={inputProps?.['aria-describedby']}
            optionalTranslation={optionalTranslation}
            isInvalid={effectiveIsInvalid}
            isRequired={isRequired}
            errorMessage={effectiveErrorMessage}
            showError={errorPlacement === 'top'}
            errorId={errorId}
            errorPrefix={errorPrefix}
            visuallyHiddenLabel={visuallyHiddenLabel}
          />
        )}

        {/* Provide input state to all subcomponents (Prefix, Suffix, Icons, Slots) */}
        {/* Subcomponents read context to adapt their rendering (e.g., disable buttons, gray icons) */}
        <InputContext.Provider
          value={{
            isDisabled,
            isInvalid: effectiveIsInvalid,
            inputId,
          }}
        >
          {/* Main input grid container */}
          {/* Uses CSS Grid to position input and all slots with dynamic spacing */}
          <div className={styles['input-field__grid']}>
            {/* Wrapper for input and all decorative/interactive elements */}
            {/* Classes include base styling + responsive variants for different breakpoints */}
            {/* Data attributes signal CSS when buttons are present (affects padding) */}
            <div
              ref={wrapperRef}
              className={[
                styles['input-field__wrapper'],
                inputStyles['input-field-variant'],
                baseClass,
                ...responsiveClasses,
              ]
                .filter(Boolean)
                .join(' ')}
              {...(hasStartSlotButton
                ? { 'data-has-start-button': 'true' }
                : {})}
              {...(hasEndSlotButton ? { 'data-has-end-button': 'true' } : {})}
            >
              {/* Render priority-based start content: Icon → Select → Slot → Prefix */}
              {/* Priority ensures only the most specific slot renders */}
              {/* e.g., if both Icon and Prefix provided, only Icon renders */}
              {resolveStartContent(
                startIconChildren,
                startSelectChildren,
                startSlotChildren,
                prefixChildren,
                { startIconRef, startSelectRef, startSlotRef, prefixRef },
                visuallyHiddenLabel,
              )}

              {/* Native HTML input element from react-aria-components */}
              {/* Receives resolved type (search inputs become text), merged inputProps, and data attributes */}
              {/* Data attributes trigger CSS selectors for disabled/invalid/required styling */}
              <AriaInput
                ref={(el) =>
                  assignInputRefs(
                    el,
                    ref,
                    inputElementRef as React.MutableRefObject<HTMLInputElement | null>,
                  )
                }
                {...inputProps}
                type={inputFieldType}
                placeholder={placeholder}
                className={inputFieldStyle}
                data-disabled={isDisabled ? true : undefined}
                data-invalid={effectiveIsInvalid ? true : undefined}
                data-required={isRequired ? true : undefined}
                disabled={isDisabled ? true : undefined}
              />

              {/* Clear button container - only renders if clearable enabled */}
              {/* Contains IconButton that clears input and refocuses field */}
              {effectiveClearable && (
                <div className={styles['input-field__clearButton']}>
                  {clearButtonElement}
                </div>
              )}

              {/* Render priority-based end content: Icon → Select → Slot → Suffix */}
              {/* Same priority logic as start content to prevent multiple elements rendering */}
              {resolveEndContent(
                endIconChildren,
                endSelectChildren,
                endSlotChildren,
                suffixChildren,
                { endIconRef, endSelectRef, endSlotRef, suffixRef },
                visuallyHiddenLabel,
              )}
            </div>
          </div>
        </InputContext.Provider>
        {/* Render bottom-positioned error if placement is 'bottom' */}
        {/* Top-positioned errors are rendered in FieldHeader component */}
        {errorPlacement === 'bottom' ? (
          <div className={styles['input-field__errorMessage']}>
            <FieldError
              id={errorId}
              fieldLabel={label}
              errorPrefix={errorPrefix}
            >
              {effectiveErrorMessage}
            </FieldError>
          </div>
        ) : null}
      </FieldComponent>
    );
  },
) as InputComponent;

Input.displayName = 'Input';

// Attach subcomponents
Input.StartSlot = InputStartSlot;
Input.EndSlot = InputEndSlot;
Input.Prefix = InputPrefix;
Input.Suffix = InputSuffix;
Input.StartSelect = InputStartSelect;
Input.EndSelect = InputEndSelect;
Input.StartIcon = InputStartIcon;
Input.EndIcon = InputEndIcon;

export default Input;
